import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, TrendingUp, Users, DollarSign, Target, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { InfoTip, RATE_FORMULAS } from "@/components/InfoTip";
import BottomNav from "@/components/BottomNav";
import { ChannelTable } from "@/components/ChannelTable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getSessionToken = () => localStorage.getItem('session_token');

export default function Analytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/analytics/lead-sources?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate('/jobs');
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${value || 0}%`;
  };

  const getSourceColor = (source) => {
    const colors = {
      google_ads: "bg-blue-100 text-blue-700 border-blue-200",
      google_organic: "bg-blue-50 text-blue-600 border-blue-100",
      facebook: "bg-indigo-100 text-indigo-700 border-indigo-200",
      facebook_organic: "bg-indigo-50 text-indigo-600 border-indigo-100",
      direct: "bg-zinc-100 text-zinc-700 border-zinc-200",
      yelp: "bg-red-100 text-red-700 border-red-200",
      nextdoor: "bg-green-100 text-green-700 border-green-200",
    };
    return colors[source] || "bg-amber-100 text-amber-700 border-amber-200";
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-zinc-100 rounded-lg"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-barlow text-xl font-bold text-zinc-900">Lead Source Analytics</h1>
            <p className="text-sm text-zinc-500">Track where your leads come from</p>
          </div>
          <button
            onClick={fetchAnalytics}
            disabled={loading}
            className="p-2 hover:bg-zinc-100 rounded-lg"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 pb-24 space-y-6">
        {/* Period Selector */}
        <div className="flex gap-2">
          {[
            { value: 7, label: "7 Days" },
            { value: 30, label: "30 Days" },
            { value: 90, label: "90 Days" },
            { value: 0, label: "All Time" }
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setPeriod(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                period === option.value
                  ? "bg-[#207024] text-white"
                  : "bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`period-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center gap-2 text-zinc-500 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-xs font-medium">Total Leads</span>
              </div>
              <p className="text-2xl font-bold text-zinc-900" data-testid="total-leads">
                {data.summary.total_leads}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center gap-2 text-zinc-500 mb-1">
                <Target className="w-4 h-4" />
                <span className="text-xs font-medium">Booked</span>
              </div>
              <p className="text-2xl font-bold text-zinc-900" data-testid="total-booked">
                {data.summary.total_booked}
              </p>
              <p className="text-xs text-zinc-500">
                {formatPercent(data.summary.overall_conversion_rate)} quote book rate <InfoTip text={RATE_FORMULAS.quote_book_rate} testid="tip-ls-quote-book" />
              </p>
            </div>

            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center gap-2 text-zinc-500 mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">Paid</span>
              </div>
              <p className="text-2xl font-bold text-zinc-900" data-testid="total-paid">
                {data.summary.total_paid}
              </p>
              <p className="text-xs text-zinc-500" data-testid="ls-paid-over-booked">
                {formatPercent(data.summary.overall_paid_over_booked_rate ?? data.summary.overall_close_rate)} Paid ÷ Booked <InfoTip text={RATE_FORMULAS.paid_over_booked} testid="tip-ls-paid-over-booked" />
              </p>
              <p className="text-[11px] text-zinc-400" data-testid="ls-close-rate-paid">
                {formatPercent(data.summary.overall_close_rate_paid || 0)} Close Rate (paid) <InfoTip text={RATE_FORMULAS.close_rate_paid} testid="tip-ls-close-paid" />
              </p>
            </div>

            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center gap-2 text-[#207024] mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium">Revenue</span>
              </div>
              <p className="text-2xl font-bold text-[#207024]" data-testid="total-revenue">
                {formatCurrency(data.summary.total_revenue)}
              </p>
            </div>
          </div>
        )}

        {/* Source Breakdown */}
        {data?.by_channel?.length > 0 && <ChannelTable rows={data.by_channel} />}
        {data?.by_last_touch_channel?.length > 0 && <ChannelTable rows={data.by_last_touch_channel} title="By marketing channel (last touch)" />}
        {data && data.by_source.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100">
              <h2 className="font-semibold text-zinc-900">Performance by Source</h2>
            </div>
            
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-zinc-600">Source</th>
                    <th className="text-center px-4 py-3 font-semibold text-zinc-600">Leads</th>
                    <th className="text-center px-4 py-3 font-semibold text-zinc-600">Booked</th>
                    <th className="text-center px-4 py-3 font-semibold text-zinc-600">Quote Book % <InfoTip text={RATE_FORMULAS.quote_book_rate} testid="tip-ls-col-book" /></th>
                    <th className="text-center px-4 py-3 font-semibold text-zinc-600">Paid</th>
                    <th className="text-right px-4 py-3 font-semibold text-zinc-600">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_source.map((source, index) => (
                    <tr key={source.source} className={index % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium border ${getSourceColor(source.source)}`}>
                          {source.display_name}
                        </span>
                        {source.campaigns.length > 0 && (
                          <div className="text-xs text-zinc-400 mt-1">
                            {source.campaigns.slice(0, 2).join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="text-center px-4 py-3 font-medium">{source.leads}</td>
                      <td className="text-center px-4 py-3">{source.booked}</td>
                      <td className="text-center px-4 py-3">
                        <span className={`font-medium ${source.conversion_rate >= 30 ? 'text-green-600' : source.conversion_rate >= 15 ? 'text-amber-600' : 'text-zinc-500'}`}>
                          {formatPercent(source.conversion_rate)}
                        </span>
                      </td>
                      <td className="text-center px-4 py-3">{source.paid}</td>
                      <td className="text-right px-4 py-3 font-semibold text-[#207024]">
                        {formatCurrency(source.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-zinc-100">
              {data.by_source.map((source) => (
                <div key={source.source} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium border ${getSourceColor(source.source)}`}>
                      {source.display_name}
                    </span>
                    <span className="font-bold text-[#207024]">{formatCurrency(source.revenue)}</span>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 text-center text-xs">
                    <div>
                      <p className="text-zinc-500">Leads</p>
                      <p className="font-semibold text-zinc-900">{source.leads}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Booked</p>
                      <p className="font-semibold text-zinc-900">{source.booked}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Book % <InfoTip text={RATE_FORMULAS.quote_book_rate} /></p>
                      <p className={`font-semibold ${source.conversion_rate >= 30 ? 'text-green-600' : 'text-zinc-900'}`}>
                        {formatPercent(source.conversion_rate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Paid</p>
                      <p className="font-semibold text-zinc-900">{source.paid}</p>
                    </div>
                  </div>
                  
                  {source.campaigns.length > 0 && (
                    <div className="text-xs text-zinc-400 mt-2">
                      Campaigns: {source.campaigns.slice(0, 2).join(", ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {data && data.by_source.length === 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-8 text-center">
            <TrendingUp className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">No lead data for this period</p>
            <p className="text-sm text-zinc-400 mt-1">
              Leads will appear here as estimates come in
            </p>
          </div>
        )}

        {/* Tips */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
          <h3 className="font-semibold text-amber-900 mb-2">Tips for Better Tracking</h3>
          <ul className="space-y-1 text-amber-800">
            <li>• Use UTM parameters in your ad links: <code className="bg-amber-100 px-1 rounded">?utm_source=google&utm_campaign=spring</code></li>
            <li>• Google Ads clicks are auto-detected via <code className="bg-amber-100 px-1 rounded">gclid</code></li>
            <li>• Facebook clicks are auto-detected via <code className="bg-amber-100 px-1 rounded">fbclid</code></li>
          </ul>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
